import React, { useEffect, useMemo, useRef, useState } from 'react';
import SortIcon from 'src/components/ui-icons/table/SortIcon';
import { focusIcon } from '../../ui-icons/FocusGop';
import { selectModelData, selectModelLoading, selectModelPagination } from '../../selectors/selectors';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { fetchData } from '../../actions/actions';
import Loading from 'src/components/Loading';
import { EmptyList } from 'src/components/EmptyList';
import { DateRangeFilterValue, FilterKey, FilterMappings, FilterValue, PlanActionsContext, SelectionFilterValue } from '../../types/types';
import { getItemsRequest } from 'src/modules/spItems/api';
import { myTaskActionsListName } from 'src/api/config/myTasksActions.config';
import { modifiedItemsState, selectGetStatus } from 'src/modules/spItems/selectors';
import { UserAvatar } from 'src/components/ui/avatar/Avatar';
import { TooltipItem } from 'src/utils/Tooltip';
import moment from 'moment';
import { selectUsers } from 'src/modules/users/selector';
import { useSearchParams } from 'react-router-dom';
import { siteUrl } from 'src/api/config';

const styles = {
  w_100: 'px-6 py-3 w-[150px] font-semibold text-left border-b border-slate-200 text-sm',
  w_200: 'px-6 py-3 w-[250px] font-semibold text-left border-b border-slate-200 text-sm',
  w_400: 'px-6 py-3 w-[400px] font-semibold text-left border-b border-slate-200 text-sm',
  dataTable: 'px-6 py-3 text-primary text-left border-b border-slate-200 text-sm',
};

const itemsPerPage = 100;
const baseFilters = { ImportantHashTag: `#${'GOPСтандарты'}*` };

interface Filters {
  direction?: SelectionFilterValue;
  block?: SelectionFilterValue;
  demandGop?: SelectionFilterValue;
  focusGop?: SelectionFilterValue;
  created?: DateRangeFilterValue;
  [key: string]: FilterValue | undefined;
}

const filterMappings: FilterMappings = {
  actions: {
    direction: 'directionID',
    block: 'blockID',
    demandGop: 'demandID',
    focusGop: 'IsFocal',
  },
  actionsSP: {
    meeting: 'Meeting',
    status: 'Status',
  },
};

export type FilterKeySP = 'created' | 'dataTable' | 'status' | 'meeting';
export interface AdditionalFilters {
  [key: string]:
  | number[]
  | string
  | string[]
  | {
    start?: string;
    end?: string;
  };
}

interface ContentGopProps {
  filters: Filters;
  refreshTrigger?: number;
  onDataUpdate?: (data: any[]) => void;
  context: PlanActionsContext;
}

export default function ContentGop({ filters, refreshTrigger, onDataUpdate, context }: ContentGopProps) {
  const dispatch = useAppDispatch();
  const tableRef = useRef<HTMLDivElement>(null);
  const dataGopActions = useAppSelector((state: any) => selectModelData(state, 'gop-actions'));
  const loadingDataGop = useAppSelector((state: any) => selectModelLoading(state, 'gop-actions'));
  const pagination = useAppSelector((state: any) => selectModelPagination(state, 'gop-actions'));
  const users: any = useAppSelector(selectUsers);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [allData, setAllData] = useState<any>([]);
  const [getActions, setGetActions] = useState(false);
  const [nextPageActions, setNextPageActions] = useState<SP.ListItemCollectionPosition | undefined>(undefined);
  const [localActions, setLocalActions] = useState<any[]>([]);
  const [allMatchedActions, setAllMatchedActions] = useState<any[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingSP, setIsLoadingSP] = useState(false);
  const [query, setQuery] = useSearchParams();
  const [sortOrderDate, setSortOrderDate] = useState<'asc' | 'desc'>('desc');
  const [sortOrderDoing, setSortOrderDoing] = useState<'asc' | 'desc'>('asc');

  const getFormattedFilters = React.useCallback(() => {
    if (!filters) {
      return { filters: {}, mainFilters: baseFilters };
    }
    const filtersContentGop: Record<string, any> = {};
    if (filters.created?.startDate) {
      filtersContentGop['filters[createdAt][$gte]'] = filters.created.startDate;
    }
    if (filters.created?.endDate) {
      filtersContentGop['filters[createdAt][$lte]'] = filters.created.endDate;
    }
    function isFilterKey(key: string): key is FilterKey {
      return key in filterMappings.actions;
    }
    function isSelectionFilter(filter: FilterValue): filter is SelectionFilterValue {
      return 'selected' in filter;
    }

    Object.entries(filterMappings.actions).forEach(([filterKey, mappedKey]) => {
      if (isFilterKey(filterKey) && filters[filterKey]) {
        if (isSelectionFilter(filters[filterKey]) && filters[filterKey].selectedIds?.length > 0) {
          if (mappedKey === 'IsFocal') {
            const selectedIds = filters[filterKey].selectedIds;
            if (selectedIds.length === 2 || selectedIds.length === 0) {
            } else if (selectedIds.length === 1) {
              const selectedId = Number(selectedIds[0]);
              if (selectedId === 1) {
                filtersContentGop[`filters[${mappedKey}][$eq]`] = true;
              } else if (selectedId === 2) {
                filtersContentGop[`filters[${mappedKey}][$eq]`] = false;
              }
            }
          } else if (filters[filterKey].selectedIds.length === 1) {
            filtersContentGop[`filters[${mappedKey}][$eq]`] = filters[filterKey].selectedIds[0];
          } else {
            filtersContentGop[`filters[${mappedKey}][$in]`] = filters[filterKey].selectedIds;
          }
        }
      }
    });

    if (context?.selectedFactory?.id) {
      filtersContentGop['filters[factory][$eq]'] = context.selectedFactory.id;
    }
    if (context?.selectedGroup?.id) {
      filtersContentGop['filters[group][$eq]'] = context.selectedGroup.id;
    }
    if (context?.selectedGop?.id) {
      filtersContentGop['filters[gop][$eq]'] = context.selectedGop.id;
    }

    const additionalFilters: AdditionalFilters = {
      Created: {
        start: filters.created?.startDate,
        end: filters.created?.endDate,
      },
      DateForDone: {
        start: filters.dataTable?.startDate,
        end: filters.dataTable?.endDate,
      },
    };

    if (filters.responsible && filters.responsible?.selected?.length > 0) {
      additionalFilters.ResponsibleEmail = filters.responsible.selected;
    }

    function isFilterKeySP(key: string): key is FilterKeySP {
      return key in filters;
    }

    function isSelectionFilterSP(filter: FilterValue): filter is SelectionFilterValue {
      return 'selected' in filter;
    }

    Object.entries(filterMappings.actionsSP).forEach(([filterKey, mappedKey]) => {
      if (isFilterKeySP(filterKey) && filters[filterKey] && isSelectionFilterSP(filters[filterKey]) && filters[filterKey].selectedIds?.length > 0) {
        additionalFilters[mappedKey] = filters[filterKey].selectedIds;
      }
    });
    return {
      filters: filtersContentGop,
      mainFilters: { ...baseFilters, ...additionalFilters },
    };
  }, [filters, context]);

  useEffect(() => {
    if (!filters) return;
    setIsInitialLoading(true);
    setCurrentPage(1);
    setHasMoreData(true);
    setAllData([]);
    setAllMatchedActions([]);
    setLocalActions([]);
    setGetActions(false);
    setNextPageActions(undefined);

    const { filters: apiFilters } = getFormattedFilters();
    dispatch(fetchData('gop-actions', apiFilters, { page: 1, pageSize }));
  }, [dispatch, filters, pageSize, context]);

  useEffect(() => {
    if (loadingDataGop || !hasMoreData) return;
    if (!pagination || pagination.page < pagination.pageCount) {
      const { filters: apiFilters } = getFormattedFilters();
      const nextPage = currentPage + 1;
      dispatch(fetchData('gop-actions', apiFilters, { page: nextPage, pageSize }));
    }
  }, [currentPage, hasMoreData, pagination, filters, context]);

  useEffect(() => {
    const { mainFilters } = getFormattedFilters();
    let cancelled = false;

    async function loadAllSPPages() {
      try {
        setIsLoadingSP(true);
        let allActions: any[] = [];
        let nextPage: SP.ListItemCollectionPosition | undefined = undefined;
        let pageNum = 0;

        do {
          pageNum++;
          const actionsResponse: any = await getItemsRequest({
            listName: myTaskActionsListName,
            filters: mainFilters,
            rowLimit: itemsPerPage,
            sortOrder: { name: 'ID', desc: true },
            nextPage,
          });

          if (cancelled) return;

          const modifiedActions = modifiedItemsState(actionsResponse.items, myTaskActionsListName);
          allActions = [...allActions, ...modifiedActions];
          nextPage = actionsResponse.nextPage ?? undefined;

          setLocalActions([...allActions]);
          setGetActions(true);
          setNextPageActions(nextPage);
        } while (nextPage);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
      } finally {
        if (!cancelled) {
          setIsLoadingSP(false);
        }
      }
    }

    loadAllSPPages();
    return () => { cancelled = true; };
  }, [filters, context]);

  useEffect(() => {
    if (loadingDataGop) return;
    if (dataGopActions && dataGopActions.length > 0) {
      setAllData((prev: any) => {
        const existingIds = new Set(prev.map((item: any) => item.id));
        const newItems = dataGopActions.filter((item: any) => !existingIds.has(item.id));
        return [...prev, ...newItems];
      });
    }

    if (pagination) {
      setCurrentPage(pagination.page);
      setHasMoreData(pagination.page < pagination.pageCount);
    } else if (!dataGopActions || dataGopActions.length < pageSize) {
      setHasMoreData(false);
    }
  }, [dataGopActions, loadingDataGop, pagination, pageSize]);

  useEffect(() => {
    if (!loadingDataGop && !isLoadingSP && getActions) {
      setIsInitialLoading(false);
    }
  }, [loadingDataGop, isLoadingSP, getActions]);

  const calculateMatchedActions = useMemo(() => {
    if (allData.length === 0 || localActions.length === 0) {
      return [];
    }

    const actionsByGopId = new Map<number, any[]>();
    localActions.forEach((action) => {
      const matches = action.ImportantHashTag.matchAll(/#GOPСтандарты_(\d+)\b/g);
      for (const match of matches) {
        const gopId = Number(match[1]);
        if (!actionsByGopId.has(gopId)) actionsByGopId.set(gopId, []);
        actionsByGopId.get(gopId)!.push(action);
      }
    });

    const uniqueActions = new Map();
    allData.forEach((gopAction: any) => {
      const matched = actionsByGopId.get(gopAction.id) || [];
      matched.forEach((action: any) => {
        uniqueActions.set(action.ID, { ...gopAction, ...action });
      });
    });

    const result = Array.from(uniqueActions.values());
    return result;
  }, [allData, localActions]);

  useEffect(() => {
    const newActions = calculateMatchedActions;
    setAllMatchedActions(newActions);
  }, [calculateMatchedActions]);

  const toggleSortDate = () => {
    setSortOrderDate((prevOrder) => {
      const newOrder = prevOrder === 'desc' ? 'asc' : 'desc';
      const sortedActions = [...allMatchedActions].sort((a, b) => {
        const dateA = a.Created.valueOf();
        const dateB = b.Created.valueOf();
        return newOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
      setAllMatchedActions(sortedActions);
      return newOrder;
    });
  };

  const toggleSortDoing = () => {
    setSortOrderDoing((prevOrder) => {
      const newOrder = prevOrder === 'desc' ? 'asc' : 'desc';
      const sortedActions = [...allMatchedActions].sort((a, b) => {
        const dateA = a.DateForDone.valueOf();
        const dateB = b.DateForDone.valueOf();
        return newOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
      setAllMatchedActions(sortedActions);
      return newOrder;
    });
  };

  const openDocumentCard = (id: number) => {
    // const newSearchParams = new URLSearchParams(query);
    // newSearchParams.set('actionCard', id.toString());
    // setQuery(newSearchParams);
    window.open(`${siteUrl}/dashboard/3?actionCard=${id.toString()}`, "_blank");
  };

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      setCurrentPage(1);
      setHasMoreData(true);
      setAllData([]);
      setAllMatchedActions([]);
      setLocalActions([]);
      setGetActions(false);
      setNextPageActions(undefined);

      const { filters: apiFilters } = getFormattedFilters();
      dispatch(fetchData('gop-actions', apiFilters, { page: 1, pageSize }));
    }
  }, [refreshTrigger]);

  useEffect(() => {
    if (onDataUpdate) {
      onDataUpdate(allMatchedActions);
    }
  }, [allMatchedActions, onDataUpdate]);

  return (
    <div className='relative flex flex-col h-[calc(95vh-175px)] bg-white'>
      <div
        ref={tableRef}
        className='flex-1 no-outer-borders w-[100%] overflow-x-auto overflow-y-auto scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB] rounded-md shadow mt-2'
      >
        <table className='w-[100%] table-fixed border-collapse'>
          <thead className='text-s bg-white text-black sticky top-0 z-10'>
            <tr className='border-b border-slate-200'>
              <th scope='col' className={styles.w_200}>
                Направление
              </th>
              <th scope='col' className={styles.w_200}>
                Блок
              </th>
              <th scope='col' className={styles.w_400}>
                Требование
              </th>
              <th scope='col' className={styles.w_100}>
                Фокусный
              </th>
              <th scope='col' className={styles.w_200} onClick={toggleSortDate}>
                <span className='flex cursor-pointer'>
                  <span className='mr-3'>Дата создания</span>
                  <SortIcon sortDirection={sortOrderDate} />
                </span>
              </th>
              <th scope='col' className={styles.w_400}>
                Описание действий
              </th>
              <th scope='col' className={styles.w_200}>
                Отвественный
              </th>
              <th scope='col' className={styles.w_200} onClick={toggleSortDoing}>
                <span className='flex cursor-pointer'>
                  <span className='mr-3'>Срок выполнения</span>
                  <SortIcon sortDirection={sortOrderDoing} />
                </span>
              </th>
              <th scope='col' className={styles.w_100}>
                Статус
              </th>
              <th scope='col' className={styles.w_400}>
                Название совещания
              </th>
            </tr>
          </thead>
          <tbody>
            {allMatchedActions.length > 0 &&
              allMatchedActions
                .filter((action, index, self) => index === self.findIndex((a) => a.ID === action.ID))
                .map((action: any) => (
                  <tr key={action.ID} className='bg-white hover:bg-slate-50 cursor-pointer h-[70px] border-b border-slate-200' onClick={() => openDocumentCard(action.ID)}>
                    <td className={styles.dataTable}>{action.directionTitle || '-'}</td>
                    <td className={styles.dataTable}>{action.blockTitle || '-'}</td>
                    <td className={styles.dataTable}>{action.demandTitle || '-'}</td>
                    <td className={styles.dataTable}>{action.IsFocal && <div>{focusIcon}</div>}</td>
                    <td className={styles.dataTable}>{action.Created.format('DD.MM.YYYY')}</td>

                    <td className={styles.dataTable}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: action.Description.replace(/(^|\W)(#[а-я\w][а-я\w-]*)/gi, '<span style="color: rgb(0,98,155)">$&</span>'),
                        }}
                      />
                    </td>
                    <td className={`${styles.dataTable}`}>
                      <div className='flex items-center justify-between w-full'>
                        <span className='flex items-center'>
                          <TooltipItem className='-translate-x-1' position='top-right' tooltipsText={users[action.Responsible.Email]?.displayName}>
                            <UserAvatar
                              avatar={users[action.Responsible.Email]?.avatar}
                              shortName={users[action.Responsible.Email]?.shortName}
                              displayName={users[action.Responsible.Email]?.displayName}
                              email={action.Responsible.Email}
                            />
                          </TooltipItem>
                          <span className='ml-6'>{users[action.Responsible.Email]?.displayName}</span>
                        </span>
                      </div>
                    </td>
                    <td className={styles.dataTable}>{action.DateForDone.format('DD.MM.YYYY')}</td>
                    <td className={styles.dataTable}>
                      <div className='flex items-center'>
                        <svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10' fill='none'>
                          <circle cx='5' cy='5' r='5' fill={action.Status?.Metadata?.Color} />
                        </svg>
                        <span className={`ml-2 text-[${{ color: action.Status?.Metadata?.Color }}]`}>{action.Status?.Title}</span>
                      </div>
                    </td>
                    <td
                      className={`font-normal !text-[rgb(5,37,122)] ${styles.dataTable}`}
                    // onClick={(e) => {
                    //   e.stopPropagation();
                    //   openMeeting(action.Meeting.Title);
                    // }}
                    >
                      {action.Meeting.Title}
                    </td>
                  </tr>
                ))}

            {!isInitialLoading && !loadingDataGop && !isLoadingSP && !nextPageActions && allMatchedActions.length === 0 && (
              <tr>
                <td colSpan={3}>
                  <EmptyList title='Действия не найдены' />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className='pt-2'>
        {(isInitialLoading || loadingDataGop || isLoadingSP || (getActions && !!nextPageActions)) && (
          <div className='flex justify-center w-full bg-[#E5E7EB] text-black text-center py-2 rounded-md'>
            <span className='mr-2'>
              <Loading />
            </span>
            Формируется список, пожалуйста подождите
          </div>
        )}

        {!isInitialLoading && !loadingDataGop && !isLoadingSP && !hasMoreData && !nextPageActions && allMatchedActions.length > 0 && (
          <div className='w-full bg-[#012169] text-white text-center py-2 rounded-md'>Список сформирован</div>
        )}
      </div>
    </div>
  );
}
